import type { Platform } from "@newform-monorepo/db";
import { tiktokScopeName } from "./scopes/tiktok";

/**
 * One thing an ad-platform connection must have for a feature to work.
 *
 * Two kinds are checkable from the stored account row alone (`token`,
 * `scope`, `googleManagerAccess`); the rest need a live call to the platform
 * and are only evaluated at `depth: "live"`. Add a kind here when a feature
 * depends on something none of these describe, then teach
 * `evaluate.ts` (stored) or a probe in `probes/` (live) to check it.
 */
export type Requirement =
  /** A credential is stored. NewForm's own `active` switch is not access. */
  | { kind: "token" }
  /**
   * One OAuth permission. `anyOf` lists ids that each grant it, most
   * canonical first: the first one is what a reconnect asks for.
   */
  | { kind: "scope"; anyOf: readonly string[] }
  /** The connected user's role on the ad account allows advertising. */
  | { kind: "adAccountRole"; role: "ADVERTISE" }
  /** The platform accepts a campaign write (Meta validate-only probe). */
  | { kind: "campaignWrite" }
  /** A Facebook Page the connection can promote. */
  | { kind: "metaPage"; promotable: true }
  /**
   * An Instagram account the connection can act for, found through a Page,
   * a Business Manager, or either.
   */
  | {
      kind: "instagramIdentity";
      via: "page" | "businessManager" | "either";
    }
  /** The connection can read the Business Manager that owns the account. */
  | { kind: "businessManagerAccess" }
  /** A Google Ads manager (login customer) account is set and reachable. */
  | { kind: "googleManagerAccess" }
  /** A TikTok identity (custom or TikTok account) ads can run under. */
  | { kind: "tiktokIdentity" }
  /** A Snapchat ad-account or organization role that can manage ads. */
  | { kind: "snapchatRole" }
  /** The advertiser can authorize Spark Ads posts. */
  | { kind: "sparkAuth" };

export type RequirementKind = Requirement["kind"];

/** Kinds the stored account row is enough to evaluate. */
export const STORED_REQUIREMENT_KINDS: ReadonlySet<RequirementKind> = new Set([
  "token",
  "scope",
  "googleManagerAccess",
]);

export function scope(...anyOf: string[]): Requirement {
  return { kind: "scope", anyOf };
}

export const TOKEN: Requirement = { kind: "token" };

/** A stable key for a requirement, for de-duplication and live results. */
export function requirementKey(requirement: Requirement): string {
  switch (requirement.kind) {
    case "scope":
      return `scope:${requirement.anyOf.join("|")}`;
    case "adAccountRole":
      return `adAccountRole:${requirement.role}`;
    case "instagramIdentity":
      return `instagramIdentity:${requirement.via}`;
    default:
      return requirement.kind;
  }
}

function scopeLabel(platform: Platform, id: string): string {
  return platform === "TIKTOK" ? `${tiktokScopeName(id)} (${id})` : id;
}

/** What the requirement asks for, as a phrase: "the ads_management permission". */
export function describeRequirement(
  platform: Platform,
  requirement: Requirement,
): string {
  switch (requirement.kind) {
    case "token":
      return "a working connection";
    case "scope": {
      const [primary, ...alternatives] = requirement.anyOf;
      const label = primary ? scopeLabel(platform, primary) : "a permission";
      return alternatives.length === 0
        ? `the ${label} permission`
        : `the ${label} permission (or ${alternatives
            .map((id) => scopeLabel(platform, id))
            .join(" or ")})`;
    }
    case "adAccountRole":
      return "a role on the ad account that can advertise";
    case "campaignWrite":
      return "permission to create campaigns on the ad account";
    case "metaPage":
      return "a Facebook Page the connection can promote";
    case "instagramIdentity":
      return requirement.via === "businessManager"
        ? "an Instagram account owned by the Business Manager"
        : requirement.via === "page"
          ? "an Instagram account linked to a Page"
          : "an Instagram account the connection can act for";
    case "businessManagerAccess":
      return "access to the Business Manager that owns the ad account";
    case "googleManagerAccess":
      return "a Google Ads manager account";
    case "tiktokIdentity":
      return "a TikTok identity to run ads under";
    case "snapchatRole":
      return "a Snapchat role that can manage ads";
    case "sparkAuth":
      return "permission to authorize Spark Ads posts";
  }
}
